import { Box, Divider, Grid, Slider, TextField, Typography } from "@mui/material"
import { HeaderFiltro } from "../header-filtro"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBR from 'date-fns/locale/pt-BR';
import { useAppSelector } from "../../../../../../hooks/store-hooks";
import { ValueLabelComponent, valuetext } from "../../../../../../../utils";
import { useProjetosService } from "../../../../../../../use-cases/geo-veles";
import { useUIService } from "../../../../../../../use-cases/ui";
import { theme } from "../../../../../../styles";
import { useRef, useCallback, useMemo } from "react";

export const FiltroIdade = () => {
    // State
    const { filtrosSelecionados } = useAppSelector((state) => state.projetos)

    // Refs para controlar se os campos foram tocados
    const minDateFieldTouched = useRef(false)
    const maxDateFieldTouched = useRef(false)

    // Hook
    const {
        setMaxIdadeFilter,
        setMinIdadeFilter,
        setMaxDataNascimentoFilter,
        setMinDataNascimentoFilter,
        resetIdadeFilter,
        resetDataNascimentoFilter
    } = useProjetosService()
    const { setAlert } = useUIService()

    // Utils - Calcula as datas limite: mínimo 18 anos e máximo 120 anos
    // Usando useMemo para evitar recriação em cada render
    const today = useMemo(() => new Date(), []);
    const maxDate = useMemo(() =>
        new Date(today.getFullYear() - 18, today.getMonth(), today.getDate()),
        [today]
    );
    const minDate = useMemo(() =>
        new Date(today.getFullYear() - 120, today.getMonth(), today.getDate()),
        [today]
    );

    // Função para verificar se a data é válida e completa
    const isValidCompleteDate = useCallback((date: Date | null | undefined): boolean => {
        if (!date) return false;
        if (isNaN(date.getTime())) return false;

        const year = date.getFullYear();
        return year >= 1000 && year <= 9999;
    }, [])

    // Função para lidar com mudança de data mínima
    const handleMinDateChange = useCallback((newValue: Date | null, context: any) => {
        if (context.validationError === 'invalidDate') {
            return;
        }

        // Marca que o campo foi tocado quando há uma data válida e completa
        if (isValidCompleteDate(newValue)) {
            minDateFieldTouched.current = true;

            // Validações apenas se a data estiver completa
            if (newValue! < minDate) {
                setAlert({
                    message: 'A pessoa deve ter no máximo 120 anos',
                    type: 'error'
                })
                return;
            }

            if (newValue! > maxDate) {
                setAlert({
                    message: 'A pessoa deve ter no mínimo 18 anos',
                    type: 'error'
                })
                return;
            }

            // Verifica conflito com data máxima se ela existir
            if (filtrosSelecionados?.dataNascimento?.max &&
                newValue! > new Date(filtrosSelecionados.dataNascimento.max)) {
                setAlert({
                    message: 'Data inválida',
                    type: 'error'
                })
                return;
            }

            setMinDataNascimentoFilter(newValue || undefined)
            if (filtrosSelecionados?.idade?.max || filtrosSelecionados?.idade?.min) {
                resetIdadeFilter()
            }
        } else if (!newValue) {
            // Se o campo for limpo
            minDateFieldTouched.current = false;
            setMinDataNascimentoFilter(undefined)
        }
    }, [filtrosSelecionados, minDate, maxDate, setAlert, setMinDataNascimentoFilter, resetIdadeFilter, isValidCompleteDate])

    // Função para lidar com mudança de data máxima
    const handleMaxDateChange = useCallback((newValue: Date | null, context: any) => {
        if (context.validationError === 'invalidDate') {
            return;
        }

        // Marca que o campo foi tocado quando há uma data válida e completa
        if (isValidCompleteDate(newValue)) {
            maxDateFieldTouched.current = true;

            // Validações apenas se a data estiver completa
            if (newValue! < minDate) {
                setAlert({
                    message: 'A pessoa deve ter no máximo 120 anos',
                    type: 'error'
                })
                return;
            }

            if (newValue! > maxDate) {
                setAlert({
                    message: 'A pessoa deve ter no mínimo 18 anos',
                    type: 'error'
                })
                return;
            }

            // Verifica conflito com data mínima se ela existir
            if (filtrosSelecionados?.dataNascimento?.min &&
                newValue! < new Date(filtrosSelecionados.dataNascimento.min)) {
                setAlert({
                    message: 'Data inválida',
                    type: 'error'
                })
                return;
            }

            setMaxDataNascimentoFilter(newValue || undefined)
            if (filtrosSelecionados?.idade) {
                resetIdadeFilter()
            }
        } else if (!newValue) {
            // Se o campo for limpo
            maxDateFieldTouched.current = false;
            setMaxDataNascimentoFilter(undefined)
        }
    }, [filtrosSelecionados, minDate, maxDate, setAlert, setMaxDataNascimentoFilter, resetIdadeFilter, isValidCompleteDate])

    return (
        <>
            <HeaderFiltro titulo="Idade / Data de Nascimento"
                descricao={<>
                    <Typography>
                        Esta opção permite buscar pessoas pela idade ou data de nascimento, entre um período inicial ou final.
                    </Typography>
                    <Typography
                        style={{
                            marginBottom: '10px'
                        }}
                    >
                        Não é obrigatório selecionar uma data inicial e final.
                    </Typography>
                    <Typography>
                        Caso nenhuma opção seja selecionada, consideraremos todas as opções.
                    </Typography>
                </>}
            />

            <Box p={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6" color="primary" fontWeight="bold" textTransform={"uppercase"}>
                            Idade:
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            value={filtrosSelecionados?.idade?.min ?? ''}
                            InputLabelProps={{
                                shrink: true
                            }}
                            fullWidth
                            label="Idade mínima"
                            onChange={(e) => {
                                const regexValue = e.target.value.replace(/\D/g, '')
                                if (regexValue.length === 0) {
                                    setMinIdadeFilter(undefined)
                                    return
                                }

                                setMinIdadeFilter(Number(regexValue))

                                if (filtrosSelecionados?.dataNascimento) {
                                    resetDataNascimentoFilter()
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            value={filtrosSelecionados?.idade?.max ?? ''}
                            InputLabelProps={{
                                shrink: true
                            }}
                            fullWidth
                            label="Idade máxima"
                            onChange={(e) => {
                                const regexValue = e.target.value.replace(/\D/g, '')
                                if (regexValue.length === 0) {
                                    setMaxIdadeFilter(undefined)
                                    return
                                }

                                setMaxIdadeFilter(Number(regexValue))

                                if (filtrosSelecionados?.dataNascimento) {
                                    resetDataNascimentoFilter()
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} my={'15px'}>
                        <Slider
                            getAriaValueText={valuetext}
                            step={1}
                            marks={[
                                {
                                    value: 18,
                                    label: '18',
                                },
                                {
                                    value: 40,
                                    label: '40'
                                },
                                {
                                    value: 60,
                                    label: '60',
                                },
                                {
                                    value: 80,
                                    label: '80',
                                },
                                {
                                    value: 120,
                                    label: '120'
                                }
                            ]}
                            min={18}
                            max={120}
                            value={[filtrosSelecionados?.idade?.min || 18, filtrosSelecionados?.idade?.max || 120]}
                            onChange={(e, value: any) => {
                                setMinIdadeFilter(Number(value[0]))
                                setMaxIdadeFilter(Number(value[1]))

                                if (filtrosSelecionados?.dataNascimento) {
                                    resetDataNascimentoFilter()
                                }
                            }}
                            slots={{
                                valueLabel: ValueLabelComponent
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider color={theme.palette.primary.main} sx={{ my: 3 }} />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" color="primary" fontWeight="bold" textTransform={"uppercase"}>
                            Data de Nascimento:
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <LocalizationProvider
                            dateAdapter={AdapterDateFns}
                            adapterLocale={ptBR}>
                            <DatePicker
                                label="Data início"
                                value={filtrosSelecionados?.dataNascimento?.min ? new Date(filtrosSelecionados?.dataNascimento?.min) : null}
                                onChange={handleMinDateChange}
                                minDate={minDate}
                                maxDate={maxDate}
                                disableFuture
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={6}>
                        <LocalizationProvider
                            dateAdapter={AdapterDateFns}
                            adapterLocale={ptBR}>
                            <DatePicker
                                disableFuture
                                label="Data fim"
                                value={filtrosSelecionados?.dataNascimento?.max ? new Date(filtrosSelecionados?.dataNascimento?.max) : null}
                                onChange={handleMaxDateChange}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </LocalizationProvider>
                    </Grid>
                </Grid>
            </Box>
        </>
    )
}